<template>
  <div :class="[containerClass]">
    <label
      v-if="label"
      :id="`input-${name}-label`"
      :for="id"
      :class="[labelClass || 'form-label fs-6 fw-bolder text-black']"
    >
      {{ label }} <span v-if="isRequired" class="text-danger">*</span>
    </label>

    <div :class="['tagify-wrapper', IsInputGroup ? 'input-group' : '']" style="position: relative;">
      <input
        ref="tagInput"
        type="text"
        class="form-control tagify"
        :class="[getClasses(size), inputClass]"
        :name="id"
        :id="id"
        :placeholder="placeholder"
        :required="isRequired"
        :aria-label="label"
        :disabled="isDisabled"
      />
      <small :id="`${id}-help-block`" class="form-text text-warning">{{ helperText }}</small>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import Tagify from '@yaireo/tagify'
import '@yaireo/tagify/dist/tagify.css'

const props = defineProps({
  name: { type: String, required: true },
  label: String,
  labelClass: String,
  inputClass: String,
  containerClass: { type: String, default: 'col-12 mb-10' },
  placeholder: String,
  helperText: String,
  size: { type: String, default: 'md' },
  isRequired: Boolean,
  isDisabled: Boolean,
  IsInputGroup: Boolean,
  includeSelectedTags: {type: Boolean, default: false},
  closeOnSelect: {type: Boolean, default: false},
  modelValue: Array,
  existingTags: {
    type: Array,
    default: () => []
  },
  dataPattern: {
    type: Object,
    default: () => new RegExp('^.*$')
  }
})

const emit = defineEmits(['update:modelValue'])

const tagInput = ref(null)
const tagify = ref(null)

const id = computed(() => `input-tag-${props.name}-id`)

const getClasses = size => (size ? `form-control-${size}` : '')

watch(() => props.modelValue, (newVal) => {
  if (tagify.value) {
    const currentValues = tagify.value.value.map(tag => tag.value)
    const newValues = (newVal || [])

    if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
      tagify.value.removeAllTags()
      tagify.value.addTags(newValues)
    }
  }
}, { immediate: true })

function initTagify() {
  const whitelist = (props.existingTags || []).map(tag =>
    typeof tag === 'string' ? tag : tag?.value || ''
  )

  if (tagify.value) tagify.value.destroy()

  tagify.value = new Tagify(tagInput.value, {
    pattern: props.dataPattern,
    whitelist,
    enforceWhitelist: false,
    keepInvalidTags: true,
    maxTags: 20,
    dropdown: {
      maxItems: 30,
      classname: 'tagify__dropdown',
      enabled: 1,
      closeOnSelect: props.closeOnSelect,   
      includeSelectedTags: props.includeSelectedTags
    }
  })

  tagify.value
    .on('add', updateModel)
    .on('remove', updateModel)
}

function handleClickOutside(){

}

function updateModel(e) {
  e.preventDefault()
  const values = e.detail.tagify.value.map(tag => tag.value)
  emit('update:modelValue', values)
}

onMounted(() => {
  initTagify()
    document.addEventListener('click', handleClickOutside);
})

onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);

  if (tagify.value) tagify.value.destroy()
})

watch(() => props.existingTags, newTags => {
  if (tagify.value) {
    const safeTags = (newTags || []).map(tag =>
      typeof tag === 'string' ? tag : tag?.value || ''
    ).filter(Boolean)

    tagify.value.settings.whitelist = safeTags
    tagify.value.dropdown.refilter()
  }
}, { immediate: true })
</script>

<style scoped>
.tagify__dropdown {
  z-index: 9999 !important;
  position: absolute !important;
}
</style>
